// ──────────────────────────────────────────────────────────────────────
// LumiObserver — global event channel every interactive writes to.
// The tutor watches this stream and only speaks when a meaningful
// pattern emerges. Events are plain JSON so they can be stringified
// straight into an API prompt.
//
// Event shape:
//   {
//     interactiveId: string,      // e.g. 'integration_solver_p1'
//     type:          string,      // 'step_entered' | 'step_rejected' | 'slider_moved' | …
//     payload:       object,      // type-specific
//     timestamp:     number,      // ms epoch — added by observer
//   }
// ──────────────────────────────────────────────────────────────────────

const MAX_LOG = 50;

class LumiObserver {
  constructor() {
    this.events = [];
    this.subscribers = new Set();
    this._idleTimers = new Map();       // interactiveId -> { t30, t60, t120 }
    this._lastEventByInteractive = new Map();
    this._idleThresholds = { idle_detected: 30, idle_extended: 60, student_stuck: 120 };
  }

  // Called by interactive components. Pushes the event, stamps it,
  // trims the ring, and kicks idle timers.
  emit(event) {
    if (!event || typeof event !== 'object' || !event.type) return;
    const stamped = { ...event, timestamp: Date.now() };
    this.events.push(stamped);
    if (this.events.length > MAX_LOG) this.events.shift();

    const { interactiveId } = stamped;
    if (interactiveId) {
      this._lastEventByInteractive.set(interactiveId, stamped);
      // Any real event (not an idle synthetic one) resets idle timers.
      if (!stamped.type.startsWith('idle_') && stamped.type !== 'student_stuck') {
        this._resetIdleTimers(interactiveId);
      }
    }

    for (const fn of this.subscribers) {
      try { fn(stamped); } catch (_) { /* isolated subscriber failure */ }
    }
  }

  subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  // Most recent N events (oldest first).
  getRecent(n = 20) {
    if (n >= this.events.length) return [...this.events];
    return this.events.slice(-n);
  }

  // Events scoped to a single interactive.
  getRecentForInteractive(interactiveId, n = 20) {
    const all = this.events.filter(e => e.interactiveId === interactiveId);
    return all.slice(-n);
  }

  // Explicit reset (e.g. when a new interactive mounts).
  reset() {
    this.events = [];
    this._lastEventByInteractive.clear();
    for (const timers of this._idleTimers.values()) {
      for (const t of Object.values(timers)) if (t) clearTimeout(t);
    }
    this._idleTimers.clear();
  }

  // Idle timer helpers — only armed while an interactive is mounted.
  armIdleTimers(interactiveId) {
    if (!interactiveId) return;
    this._resetIdleTimers(interactiveId);
  }

  disarmIdleTimers(interactiveId) {
    const t = this._idleTimers.get(interactiveId);
    if (!t) return;
    for (const id of Object.values(t)) if (id) clearTimeout(id);
    this._idleTimers.delete(interactiveId);
  }

  _resetIdleTimers(interactiveId) {
    this.disarmIdleTimers(interactiveId);
    const timers = {};
    timers.t30 = setTimeout(() => {
      this.emit({ interactiveId, type: 'idle_detected', payload: { secondsIdle: this._idleThresholds.idle_detected } });
    }, this._idleThresholds.idle_detected * 1000);
    timers.t60 = setTimeout(() => {
      this.emit({ interactiveId, type: 'idle_extended', payload: { secondsIdle: this._idleThresholds.idle_extended } });
    }, this._idleThresholds.idle_extended * 1000);
    timers.t120 = setTimeout(() => {
      this.emit({ interactiveId, type: 'student_stuck', payload: { secondsIdle: this._idleThresholds.student_stuck } });
    }, this._idleThresholds.student_stuck * 1000);
    this._idleTimers.set(interactiveId, timers);
  }
}

// Singleton — shared across the whole app so every interactive pushes to
// the same stream that LumiTutor is subscribed to.
export const lumiObserver = new LumiObserver();

// Convenience: default export for ESM style.
export default lumiObserver;
